import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight, AlertCircle, Users, Loader2, HandCoins } from "lucide-react";

/**
 * CreditReceivableStrip — Khatabook-style "You'll get" cash-flow strip.
 * Fetches GET /credit-customers/dashboard/rollup and renders 3 KPI cards:
 *   • You'll get (₹ outstanding across all active customers)
 *   • Overdue amount + count
 *   • Active customers
 * Clicking anywhere navigates to /dealer/credit-customers.
 */
export default function CreditReceivableStrip() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await api.get("/credit-customers/dashboard/rollup");
        if (alive) setData(d);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="mb-4 flex h-24 items-center justify-center rounded-2xl border border-border bg-card text-sm text-muted-foreground" data-testid="receivable-strip-loading">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading credit dashboard…
      </div>
    );
  }
  if (!data) return null;

  const cards = [
    {
      key: "receivable",
      label: "You'll get",
      value: `₹${Number(data.receivable || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`,
      Icon: HandCoins,
      accent: "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200",
      hint: "Total outstanding",
    },
    {
      key: "overdue",
      label: "Overdue",
      value: `₹${Number(data.overdue_amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`,
      Icon: AlertCircle,
      accent: data.overdue_count > 0
        ? "bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200"
        : "bg-muted text-muted-foreground",
      hint: `${data.overdue_count || 0} customer${(data.overdue_count || 0) === 1 ? "" : "s"} past due`,
    },
    {
      key: "customers",
      label: "Active customers",
      value: String(data.active_customers || 0),
      Icon: Users,
      accent: "bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-200",
      hint: "On credit",
    },
  ];

  return (
    <Link
      to="/dealer/credit-customers"
      className="group mb-4 block"
      data-testid="receivable-strip"
    >
      <div className="grid grid-cols-1 gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm transition group-hover:shadow-md sm:grid-cols-3">
        {cards.map((c) => (
          <div key={c.key} className={`flex items-center gap-3 rounded-xl px-3 py-3 ${c.accent}`} data-testid={`receivable-card-${c.key}`}>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/60 dark:bg-black/20">
              <c.Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-black uppercase tracking-widest opacity-80">{c.label}</div>
              <div className="mt-0.5 truncate font-display text-xl font-bold tracking-tight tabular-nums">
                {c.value}
              </div>
              <div className="truncate text-[10px] opacity-75">{c.hint}</div>
            </div>
            {c.key === "receivable" && (
              <ArrowUpRight className="h-4 w-4 shrink-0 opacity-40 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
            )}
          </div>
        ))}
      </div>
    </Link>
  );
}
